// ============================================================================
// LE PRISME : le logo Vyndra, en monument, aux couleurs de tous les univers
// ----------------------------------------------------------------------------
// LA DEMANDE DU DIRIGEANT (27/09/2026) : « le V de Vyndra qui possede les
// couleurs de tous les hubs, c'est justement ce logo-la qui est a conserver ».
//
// RIEN N'EST REDESSINE. C'est le composant `VyndraLogo` lui-meme, agrandi. Ses
// sept losanges sont REPEINTS par la feuille de style (une regle CSS `fill`
// l'emporte sur l'attribut `fill` d'un trace SVG) : les six losanges des ailes
// prennent les couleurs des univers selon `colorerPrisme` (ecosysteme.ts), la
// clef de voute garde sa couleur, celle du groupe.
//
// LES LIGNES DE LUMIERE. Le logo s'appelle « Pixel Prism ». Chaque losange
// prolonge ses deux aretes exterieures en traits tres fins qui s'ouvrent sur la
// page, comme la lumiere qui sort d'un prisme. Elles ne sont pas un motif pose au hasard :
// elles partent EXACTEMENT des aretes du logo, avec les memes angles, calculees
// ci-dessous depuis la geometrie du dessin (LOSANGES). Quand on vise une porte,
// les lignes et les losanges de cet univers s'allument, le reste s'efface.
//
// Purement decoratif : hors de l'arbre lu par un lecteur d'ecran, transparent
// aux clics.
// ============================================================================

import { useId, type CSSProperties } from 'react';
import { VyndraLogo } from '@/components/vyndra-logo';
import { LIGNES } from '@/components/fonds/geometrie-prisme';
import type { PrismeColore } from './ecosysteme';
import s from './connexion.module.css';

export function PrismeEcosysteme({
  prisme,
  vise,
  className,
}: {
  /** Les couleurs par losange, ou null : le logo garde alors ses couleurs. */
  prisme: PrismeColore | null;
  /** L'univers vise par une porte (slug), « vyndra » pour la clef, ou null. */
  vise: string | null;
  className?: string;
}) {
  // Identifiants propres a cette instance : un masque SVG est reference par son
  // id dans tout le document, deux prismes ne doivent pas se le disputer.
  const id = useId().replace(/:/g, '');
  const idFondu = `prisme-fondu-${id}`;
  const idMasque = `prisme-masque-${id}`;

  // Les losanges allumes : ceux du groupe vise, ou la clef pour Vyndra.
  const allumes = new Set<number>(
    vise === 'vyndra' ? [6] : vise && prisme ? prisme.groupes[vise] ?? [] : [],
  );
  const enVisee = allumes.size > 0;

  const variables: Record<string, string | number> = {};
  if (prisme) {
    prisme.losanges.forEach((couleur, i) => {
      if (couleur) variables[`--p${i}`] = couleur;
    });
  }
  for (let i = 0; i < 7; i += 1) {
    variables[`--f${i}`] = enVisee ? (allumes.has(i) ? 1 : 0.14) : 1;
  }

  return (
    <div
      className={`${s.prisme} ${className ?? ''}`}
      data-peint={prisme ? '' : undefined}
      data-vise={enVisee ? '' : undefined}
      style={variables as CSSProperties}
      aria-hidden="true"
    >
      {/* Le calque des lignes mesure 8 fois le logo, centre sur lui, dans le meme
          repere (le logo fait 200 unites) : les lignes partent exactement des
          aretes. Au-dela, le fondu les a deja eteintes. */}
      <svg className={s.rayons} viewBox="-700 -700 1600 1600" preserveAspectRatio="xMidYMid meet">
        <defs>
          {/* Les lignes s'eteignent avant d'atteindre les textes des deux
              colonnes : elles eclairent la scene, elles ne barrent rien. */}
          <radialGradient id={idFondu} cx="100" cy="100" r="760" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#fff" stopOpacity="1" />
            <stop offset="0.18" stopColor="#fff" stopOpacity="0.8" />
            <stop offset="0.5" stopColor="#fff" stopOpacity="0.22" />
            <stop offset="1" stopColor="#fff" stopOpacity="0" />
          </radialGradient>
          <mask id={idMasque} maskUnits="userSpaceOnUse" x="-700" y="-700" width="1600" height="1600">
            <rect x="-700" y="-700" width="1600" height="1600" fill={`url(#${idFondu})`} />
          </mask>
        </defs>
        <g mask={`url(#${idMasque})`}>
          {LIGNES.map((l, i) => (
            <line
              key={i}
              x1={l.x1}
              y1={l.y1}
              x2={l.x2}
              y2={l.y2}
              className={s.rayon}
              data-rang={l.rang}
              data-allume={allumes.has(l.rang) ? '' : undefined}
              // Repli : les couleurs d'origine du logo (aile gauche, aile droite,
              // clef), portees par la feuille de style, quand aucun univers n'est peint.
              style={{ stroke: `var(--p${l.rang}, var(${l.rang <= 2 ? '--aile-g' : l.rang <= 5 ? '--aile-d' : '--clef'}))` } as CSSProperties}
            />
          ))}
        </g>
      </svg>
      {/* La lueur : le MEME logo, flou, derriere le net. Chaque losange y
          projette sa couleur sur le fond. */}
      <VyndraLogo size={200} className={s.lueur} />
      <VyndraLogo size={200} className={s.logo} />
    </div>
  );
}
